// KPI Builder — recipes that draw several series side by side (compare, share).
import { filterClause, isFilterReady } from './custom';
import { groupById } from './learnerGroups';
import { selectedGroups } from './lookups';
import { clauseFor, envelope, filterFor, NUMERIC_GROUP_FIELDS, RecipeContext, RecipeResult, snake, traitClause, withoutBlank } from './queries';
import { BuilderAnswers, EsNode, GroupBy, SourceId } from './types';

type EsClause = Record<string, unknown>;

const SERIES_ALL: Record<SourceId, string> = { reg: 'Registered', enr: 'Enrolled', pass: 'Certified', rating: 'Rated', assess: 'Attempted', question: 'Answered', activity: 'Active', custom: 'Records' };

/** The interval setting the live compare/share KPIs carry. The handler reads group keys as dates when it is set, so a numeric group (level, attempt number) would turn into one merged bar: leave it out for those. */
const intervalFor = (g: GroupBy): { interval?: string } => (NUMERIC_GROUP_FIELDS.includes(g.field) ? {} : { interval: 'day' });

/** Legend names for the two bars of the compare recipe: everyone counted, then the chosen group. */
export function seriesNames(a: BuilderAnswers): [string, string] {
  const seg = groupById(a.rateSeg as string);
  const name = a.rateSeg === 'custom' ? a.custom.rateLabel || 'Matching' : seg?.short ?? seg?.label ?? '';
  return [a.custom.firstBar.trim() || SERIES_ALL[a.src as SourceId], a.custom.secondBar.trim() || name];
}

// shape of "Attempted vs Passed, by Competency": one query, two named series under one filter
export function buildCompare(c: RecipeContext): RecipeResult {
  const { a, group: g } = c;
  const [all, some] = seriesNames(a);
  const clauses = selectedGroups(a).filter((x) => x.applies.includes(c.srcId)).map((x) => clauseFor(x, c.src));
  // Blank names would show as an empty bar, so they are left out, as in the existing KPI.
  const mustNot = g.field.endsWith('.keyword') ? { must_not: [{ term: { [g.field]: '' } }] } : {};
  const series = (filter: EsClause): EsNode => ({
    filter,
    aggs: { [snake(g.id)]: { terms: { field: g.field, size: a.topN }, aggs: { count: c.metric } } },
  });
  // More bars the admin added, each its own named condition.
  const extras = a.custom.extraSeries.filter((s) => s.label.trim() && isFilterReady(s.when));
  const more = Object.fromEntries(extras.map((s) => [s.label.trim(), series(filterClause(s.when))]));
  const aggs = { filtered_data: { filter: { bool: { filter: clauses, ...mustNot } }, aggs: { [all]: series({ match_all: {} }), [some]: series(traitClause(a, c.src)), ...more } } };
  return {
    queries: [{ ...envelope(c.srcId, a), filterPath: 'aggregations.filtered_data', aggrQuery: { size: 0, aggs } }],
    cfg: { aggregationPaths: [all, some, ...extras.map((s) => s.label.trim())], orientation: 'horizontal', ...intervalFor(g), isCumulative: false, isDecimal: true },
  };
}

interface Bucket { label: string; clause: EsClause; }

/** The buckets to split into: the ones the admin defined, otherwise the data's own (such as 1 to 5 stars). */
export function bucketsOf(c: RecipeContext['a'], own: { label: string; field: string; value: string | number | boolean }[]): Bucket[] {
  const typed = c.custom.buckets.filter((b) => b.label.trim() && isFilterReady(b.when));
  if (typed.length) return typed.map((b) => ({ label: b.label.trim(), clause: filterClause(b.when) }));
  return own.map((b) => ({ label: b.label, clause: { term: { [b.field]: b.value } } }));
}

// shape of "KPI-002 Rating Distribution": one named series per bucket (1 Star … 5 Star), each split by group
export function buildShare(c: RecipeContext): RecipeResult {
  const { a, group: g } = c;
  const buckets = bucketsOf(a, c.src.buckets ?? []);
  const perGroup = { [snake(g.id)]: { terms: { field: g.field, size: a.topN } } };
  const series = Object.fromEntries(buckets.map((b) => [b.label, { filter: b.clause, aggs: perGroup }]));
  const aggs = { filtered_data: { filter: withoutBlank(filterFor(c.srcId, a), g), aggs: series } };
  return {
    queries: [{ ...envelope(c.srcId, a), filterPath: 'aggregations.filtered_data', aggrQuery: { size: 0, aggs } }],
    cfg: {
      aggregationPaths: buckets.map((b) => b.label), categoryLabel: g.cat, orientation: 'horizontal', percentageStacked: true,
      ...intervalFor(g), isCumulative: false, isDecimal: true,
    },
  };
}
